import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { get, size } from 'lodash';
import { useSelector } from 'react-redux';
import SSPList from '../../../../common/components/SSPList';
import AdvancedSearch from '../../../../common/components/AdvancedSearch/AdvancedSearch';
import { Toolbar } from '../../../../common/components/Toolbar/Toolbar';
import {
  chargerStatusOptions,
  chargingOptions,
  conditionOptions,
  getColumnClasses,
  onlineOptions,
} from '../../../../common/components/CommonTableColumns';
import { onTextFilter } from '../../../../common/components/CommonTableDataFilter/CommonTableDataFilter';

import { getChargersAdvancedSearchConfig } from '../../../ChargersList/constants';
import { getAdvancedSearchExcludedFields } from '../../../ChargersList/components/ChargersListToolbar';
import { TableSelect } from '../../../ChargersList/components/ChargersTableComponents';
import ChargerDetailsModal from '../../../ChargerDetails/components/ChargerDetailsModal';
import uiLabelsPaths from '../../../../common/services/uiFieldLabelsDictionary';
import { isOnlyMobile } from '../../../../common/services/isMobile';
import { StatusLabel, chargerStateLabels } from '../../../../common/constants/CommonUsedDropdownsStatics';
import { CHARGER_ONLINE_NO, CHARGER_ONLINE_YES, CHARGER_STATUS_UNKNOWN } from '../../../../common/constants/statuses';
import { getUser } from '../../../Login/selectors';

export const plazaChargersTableId = 'plaza';

export const plazaChargersDefaultSorting = { id: 'name', desc: false };

const getColumns = (uiLabels, openChargerModal) => [
  {
    Header: get(uiLabels, uiLabelsPaths.c_uuid, 'Charger ID'),
    id: 'c_uuid_contains',
    accessor: 'uuid',
    ...getColumnClasses({ isLink: true, isSticky: !isOnlyMobile }),
    minWidth: 100,
    Cell: ({ value }) => (
      <span
        className="link centered"
        onClick={() => {
          openChargerModal(value);
        }}
      >
        {value}
      </span>
    ),
    Filter: onTextFilter,
  },
  {
    Header: () => get(uiLabels, uiLabelsPaths.c_name, 'Charger Name'),
    id: 'name',
    minWidth: 90,
    accessor: 'chargerName',
    Filter: onTextFilter,
  },
  {
    Header: () => get(uiLabels, uiLabelsPaths.c_status, 'Charger Status'),
    id: 'status',
    minWidth: 210,
    accessor: (charger) => {
      const status = get(charger, 'status', '');
      const isOnline = get(charger, 'online');
      const value = status && status.toLowerCase();

      return isOnline ? value : 'unknown';
    },
    Cell: ({ value }) => <StatusLabel status={value} />,
    Filter: ({ filter, onChange }) => <TableSelect options={chargerStatusOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  },
  {
    Header: () => get(uiLabels, uiLabelsPaths.connector_state, 'Present Charger State'),
    id: 'charger_state',
    accessor: (charger) => {
      const chargerState = get(charger, 'chargerState');
      const isOnline = get(charger, 'online');

      return isOnline ? chargerState : CHARGER_STATUS_UNKNOWN.toUpperCase();
    },
    minWidth: 150,
    Cell: ({ value }) => <StatusLabel status={chargerStateLabels[value]} />,
    Filter: ({ filter, onChange }) => <TableSelect options={chargingOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  },
  {
    Header: () => get(uiLabels, uiLabelsPaths.c_online, 'Online'),
    id: 'online',
    minWidth: 100,
    accessor: (charger) => {
      const isOnline = get(charger, 'online');

      return isOnline ? CHARGER_ONLINE_YES : CHARGER_ONLINE_NO;
    },
    Cell: ({ value }) => <StatusLabel status={value} />,
    Filter: ({ filter, onChange }) => <TableSelect options={onlineOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  },
  {
    Header: () => get(uiLabels, uiLabelsPaths.c_condition, 'Charger Condition'),
    id: 'condition',
    accessor: 'condition',
    minWidth: 140,
    Cell: ({ value }) => <StatusLabel status={value} />,
    Filter: ({ filter, onChange }) => <TableSelect options={conditionOptions} onChange={onChange} value={filter ? filter.value : ''} />,
  },
  {
    Header: 'FW Version',
    id: 'fw_version',
    accessor: 'fwVersion',
    minWidth: 180,
    Filter: onTextFilter,
  },
];

const PlazaChargerList = ({
  config,
  evListAvailable = false,
  getChargersForPlazaDetails,
  pageSize,
  sorting,
  toggleAdvancedSearchEnabled,
  isFirstFetching,
  isFilterApplied,
  data,
  toggleFirstFetching,
  isAdvancedSearchEnabled,
  onExport,
  filter,
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [chargerId, setChargerId] = useState(null);
  const [modalTab, setModalTab] = useState('details');

  const user = useSelector(getUser);

  const excludedFields = useMemo(() => getAdvancedSearchExcludedFields(config, user), [config, user]);

  const { plazaId } = useParams();

  const openChargerModal = (openedChargerId) => {
    setChargerId(openedChargerId);
    setModalOpen(true);
  };

  const uiLabels = get(config, 'ui_labels', {});

  const columns = getColumns(uiLabels, openChargerModal);

  useEffect(() => {
    toggleFirstFetching(true);
  }, []);

  const closeChargerModal = () => {
    setModalOpen(false);
  };

  const getTableData = (id, { ...args }) => {
    getChargersForPlazaDetails(plazaId, id, { ...args });
  };

  const onFilterApply = (query) => {
    getChargersForPlazaDetails(plazaId, plazaChargersTableId, {
      page_token: 0,
      page_size: pageSize,
      filterQuery: query,
      sorting,
    });
  };

  const onToggleAdvancedSearch = (e, checked) => {
    toggleAdvancedSearchEnabled(checked);
  };

  const isShowAdvancedSearch = size(data) > 0 || isFilterApplied || filter != null;

  const advancedSearch = (
    <AdvancedSearch
      id={plazaChargersTableId}
      isShowAdvancedSearch={isShowAdvancedSearch}
      enabled={isAdvancedSearchEnabled}
      getFields={getChargersAdvancedSearchConfig}
      excludedFields={[...excludedFields, 'customer_id']}
      onApply={onFilterApply}
      onToggle={onToggleAdvancedSearch}
    />
  );

  columns.forEach((column) => {
    column.filterable = !isFilterApplied;
  });

  const handleExport = (exportProps) => {
    onExport(plazaId, exportProps);
  };

  return (
    <div className={`plaza-chargers-table${evListAvailable ? ' with-ev-list' : ''}`}>
      <Toolbar className="chargers-toolbar">
        <div className="toolbar-group">
          <div className="advanced-search">{advancedSearch}</div>
        </div>
      </Toolbar>
      <>
        <SSPList
          checkboxSticky
          isFirstFetching={isFirstFetching}
          isShowMessageEmptyList
          messageEmptyList="No chargers added to Plaza yet."
          mobileSticky
          id={plazaChargersTableId}
          onGetDataAction={getTableData}
          defaultPageSize={20}
          defaultSorted={plazaChargersDefaultSorting}
          columns={columns}
          multiselect
          exportable
          filterable={!isFilterApplied}
          onExport={handleExport}
        />
        {modalOpen && (
          <ChargerDetailsModal
            closeChangerModal={closeChargerModal}
            modal
            chargerId={chargerId}
            tab={modalTab}
            setActiveTab={setModalTab}
          />
        )}
      </>
    </div>
  );
};

PlazaChargerList.propTypes = {
  isFirstFetching: PropTypes.bool,
  pageSize: PropTypes.number,
  sorting: PropTypes.object,
  getChargersForPlazaDetails: PropTypes.func,
  toggleFirstFetching: PropTypes.func,
  onExport: PropTypes.func,
  toggleAdvancedSearchEnabled: PropTypes.func,
  isFilterApplied: PropTypes.bool,
  isAdvancedSearchEnabled: PropTypes.bool,
  data: PropTypes.array,
  filter: PropTypes.array,
};

export default PlazaChargerList;
